import { isPerfMetricsEnabled } from '../../lib/debug/performance';
import PerfMetricsState, { PerfMetricRow } from '../../lib/store-util/PerfMetricsState';

import styles from './PerfMetricsDebug.module.scss';

/** An average at or above this is worth looking at - a frame budget is ~16ms. */
const SLOW_AVG_THRESHOLD_MS = 8;
const CLOCK_PAD_SIZE = 2;

const formatClock = (timestamp: number): string => {
  const date = new Date(timestamp);
  const pad = (value: number): string => String(value).padStart(CLOCK_PAD_SIZE, '0');
  return `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

const getRowClassName = (metric: PerfMetricRow, latestUpdate: number | null): string => {
  const isStale = latestUpdate != null && metric.updatedAt < latestUpdate;
  if (isStale) {
    return `${styles.row} ${styles.staleRow}`;
  }

  return metric.avgMs >= SLOW_AVG_THRESHOLD_MS ? `${styles.row} ${styles.slowRow}` : styles.row;
};

/**
 * Aggregated market-data timings (name, count, avg/max duration, slow-sample count), refreshed
 * roughly every 2s by `flushPerfMetrics`. Rows accumulate: a metric that drops out of a window is
 * kept and greyed out, with `updated` showing when it was last seen.
 *
 * Shown only with `?debug=performance` in the URL; reload without it to hide. The panel never
 * takes pointer events, so it cannot block the UI underneath.
 */
const PerfMetricsDebug = () => {
  const { rows, updatedAt } = PerfMetricsState.use();

  if (!isPerfMetricsEnabled()) {
    return null;
  }

  const sortedRows = [ ...rows ].sort((a, b) => b.maxMs - a.maxMs);

  return (
    <div className={styles.panel}>
      <div className={styles.title}>
        <span>Performance metrics</span>
        <span>{updatedAt == null ? 'collecting…' : formatClock(updatedAt)}</span>
      </div>

      {sortedRows.length === 0 ? (
        <div className={styles.empty}>No metrics collected yet.</div>
      ) : (
        <div className={styles.table}>
          <div className={`${styles.row} ${styles.head}`}>
            <span className={styles.name}>name</span>
            <span className={styles.num}>cnt</span>
            <span className={styles.num}>avg</span>
            <span className={styles.num}>max</span>
            <span className={styles.num}>slow</span>
            <span className={styles.num}>updated</span>
          </div>
          {sortedRows.map(metric => (
            <div key={metric.name} className={getRowClassName(metric, updatedAt)}>
              <span className={styles.name} title={metric.name}>{metric.name}</span>
              <span className={styles.num}>{metric.count}</span>
              <span className={styles.num}>{metric.avgMs}</span>
              <span className={styles.num}>{metric.maxMs}</span>
              <span className={styles.num}>{metric.slowCount}</span>
              <span className={styles.num}>{formatClock(metric.updatedAt)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PerfMetricsDebug;
